import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { map, tap, Observable, of, catchError, finalize, BehaviorSubject, filter, take, switchMap } from 'rxjs';
import { Utente, LoginResponse } from '../models/user.model';
import { ConfigService } from './config.service';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

@Injectable({ providedIn: 'root' })
export class AuthService {
	private readonly USER_KEY = 'user';
	private readonly TOKEN_KEY = 'token';
	private readonly REFRESH_TOKEN_KEY = 'refreshToken';

	private http = inject(HttpClient);
	private router = inject(Router);
	private configService = inject(ConfigService);

	private get baseUrl() {
		return this.configService.configuration.apiUrl;
	}

	isAuthenticated = computed(() => !!this.userSignal());

	private userSignal = signal<Utente | null>(this.getInitialUser());
	private refreshingToken = signal(false);
	private refreshSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

	get currentUser(): Utente | null {
		return this.userSignal();
	}

	private getInitialUser(): Utente | null {
		const raw = localStorage.getItem(this.USER_KEY);
		return raw ? JSON.parse(raw) : null;
	}

	login(username: string, password: string) {
		return this.http.post<LoginResponse>(this.baseUrl + '/api/public/login', { username, password }).pipe(
			tap((res: LoginResponse) => this.handleLoginSuccess(res)),
			map((res) => res.utente),
		);
	}

	loginMicrosoftComplete(code: string) {
		return this.http.post<LoginResponse>(this.baseUrl + '/api/public/login-microsoft/complete', { code }).pipe(
			tap((res: LoginResponse) => this.handleLoginSuccess(res)),
			map((res) => res.utente),
		);
	}

	refreshToken(): Observable<any> {
		if (this.refreshingToken()) {
			return this.refreshSubject.pipe(
				filter((token) => token !== null),
				take(1),
			);
		}

		this.refreshingToken.set(true);
		this.refreshSubject.next(null);

		const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
		if (!refreshToken) {
			this.refreshingToken.set(false);
			this.logoutLocally();
			return throwError(() => new Error('No refresh token available'));
		}

		return this.http.post<LoginResponse>(this.baseUrl + '/api/public/refresh-token', { refreshToken }).pipe(
			tap((res: LoginResponse) => {
				this.handleLoginSuccess(res);
				this.refreshSubject.next(res.token);
			}),
			catchError((err) => {
				console.error('Refresh token failed', err);
				this.logoutLocally();
				return throwError(() => err);
			}),
			finalize(() => this.refreshingToken.set(false)),
		);
	}

	private handleLoginSuccess(res: LoginResponse) {
		if (res.token) {
			localStorage.setItem(this.TOKEN_KEY, res.token);
		}
		if (res.refreshToken) {
			localStorage.setItem(this.REFRESH_TOKEN_KEY, res.refreshToken);
			if (Capacitor.isNativePlatform()) {
				Preferences.set({ key: this.REFRESH_TOKEN_KEY, value: res.refreshToken });
			}
		}
		if (res.utente) {
			localStorage.setItem(this.USER_KEY, JSON.stringify(res.utente));
			this.userSignal.set(res.utente);
		}
	}

	resetPassword(username: string) {
		return this.http.get(this.baseUrl + '/api/public/reset-password', {
			params: { username },
		});
	}

	updatePassword(token: string, nuovaPassword: string, confermaNuovaPassword: string) {
		return this.http.put(this.baseUrl + `/api/public/update-password/${token}`, {
			nuovaPassword,
			confermaNuovaPassword,
		});
	}

	logout() {
		const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
		if (refreshToken) {
			this.http.post(this.baseUrl + '/api/public/logout', { refreshToken }).subscribe({
				next: () => this.logoutLocally(),
				error: () => this.logoutLocally(),
			});
		} else {
			this.logoutLocally();
		}
	}

	private logoutLocally() {
		const isWeb = this.router.url.startsWith('/web');
		localStorage.clear();
		sessionStorage.clear();
		if (Capacitor.isNativePlatform()) {
			Preferences.clear();
		}
		this.userSignal.set(null);
		this.router.navigate([isWeb ? '/web/public/login' : '/app/public/login']);
	}

	checkToken() {
		return this.http.get(this.baseUrl + '/api/check-token');
	}

	async restoreSessionFromMobile(): Promise<boolean> {
		if (!Capacitor.isNativePlatform()) return false;
		const { value } = await Preferences.get({ key: this.REFRESH_TOKEN_KEY });
		if (value) {
			localStorage.setItem(this.REFRESH_TOKEN_KEY, value);
			return true;
		}
		return false;
	}
}

import { throwError } from 'rxjs';
